import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { REQUEST_MAGIC_LINK_MUTATION } from '@graphql/mutations/session/magicLinkMutations';
import { useCallback } from 'react';

export type RequestMagicLinkResponse = {
  emailSent: boolean;
  message: string;
  errors?: Array<{ code: string; message: string; field?: string }>;
};

export const useRequestMagicLink = () => {
  const [requestMagicLinkMutation, { loading }] = useMutation<{
    requestMagicLink: RequestMagicLinkResponse;
  }>(REQUEST_MAGIC_LINK_MUTATION);

  const doRequestMagicLink = useCallback(
    async (email: string): Promise<RequestMagicLinkResponse> => {
      const response = await requestMagicLinkMutation({
        variables: { email },
      });
      const result = response.data?.requestMagicLink;

      if (!result) {
        notify.error({ title: 'Request failed', description: 'No response from server' });
        throw new Error('Magic link request failed');
      }

      if (result.errors && result.errors.length > 0) {
        notify.error({
          title: 'Failed to send magic link',
          description: result.errors[0]?.message,
        });
      } else if (!result.emailSent) {
        notify.error({
          title: 'Failed to send magic link',
          description: result.message || 'Could not send the magic link.',
        });
      } else {
        notify.success({
          title: 'Magic link sent',
          description: 'Check your email for the magic link to sign in.',
        });
      }

      return result;
    },
    [requestMagicLinkMutation]
  );

  return { doRequestMagicLink, loading };
};
